<script setup lang="ts">
import { computed, ref, type HTMLAttributes } from 'vue'
import { CalendarDate, parseDate, type DateValue } from '@internationalized/date'
import type { DateTime } from 'luxon'
import { Calendar as CalendarIcon } from '@lucide/vue'
import { DateFieldInput, DateFieldRoot, PopoverAnchor } from 'reka-ui'
import { cn } from '@ds/lib/utils'
// A pattern may use app helpers; see the note below on why neither formatting
// nor parsing may be reinvented here.
import { handleCatchedError } from '@/helpers/common'
import {
  dateFormat,
  dateTimeToString,
  getCurrentDate,
  getLastMonth,
  getLastWeek,
  getLastYear,
  parseDate as parseDateText,
  stringToLocalDateString,
} from '@/helpers/dates'
import { DATE_FORMAT_LOCALE, DATE_FORMATS, type DateFormat } from '@/helpers/dates/utils/const'
import { useNotifications } from '@/plugins/notification'
import { Button } from '../../primitives/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
} from '../../primitives/input-group'
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover'
import DateFieldCalendar from './DateFieldCalendar.vue'
import { InputSize } from '@ds/primitives/input'

/*
 * A date field: Reka's segmented DateField in the frame, its Calendar in a
 * popover. A composition, not a component, so it lives here rather than in
 * primitives/ — and one shadcn-vue does not ship at all (the registry has
 * `calendar` and nothing else), so the parts come straight from Reka.
 *
 * The model is an ISO `yyyy-mm-dd` string, not a DateValue. Reka speaks
 * @internationalized/date, the rest of this app speaks Luxon and plain ISO
 * strings, so the conversion is contained to this file instead of leaking a
 * second date library into every caller.
 *
 * THE ORDER OF THE SEGMENTS COMES FROM THE USER'S SETTING, never from this
 * component — but by way of a locale, because that is the only thing Reka
 * accepts. Intl's short-date order for three English locales happens to be
 * exactly the three formats the settings screen offers, so the setting maps
 * one to one — see DATE_FORMAT_LOCALE, which dates.spec.ts asserts against ICU
 * rather than trusting. `dateFormat` is the same ref the rest of the app formats
 * through, kept in step with userSettingsStore.getDateFormat by
 * store/user/settings.ts.
 *
 * Each segment is a `role="spinbutton"` with its own value, bounds and
 * arrow-key stepping, and the root is a `role="group"` — which is why a caller
 * labels this with `aria-labelledby` rather than a `<label for>`: a group is
 * not a labelable element.
 */

const props = defineProps<{
  /** ISO date, `yyyy-mm-dd`. Always ISO on the wire, whatever is displayed. */
  modelValue?: string
  /**
   * Pins the segment order, overriding the user's setting. For the rare screen
   * that must show one shape to everyone; leave unset and it follows the user.
   */
  format?: DateFormat
  disabled?: boolean
  hideShortcuts?: boolean
  class?: HTMLAttributes['class']
  size?: InputSize
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | undefined): void
}>()

/*
 * Attributes belong on the segment group — that is what a caller labels and
 * what carries `id` and test hooks, not the frame around it.
 */
defineOptions({ inheritAttrs: false })

const { warn } = useNotifications()

const isOpen = ref(false)

/*
 * True while the calendar's month or year select is open.
 *
 * Reka treats those as their own dismissable layer, and closing one hands focus
 * back through the popover — which reads that as a focus-outside and dismisses,
 * taking the calendar with it exactly as the user's choice lands. Portalling is
 * not the culprit (the list renders wherever we ask); the dismissal is a focus
 * event, so it has to be refused explicitly.
 */
const isMenuOpen = ref(false)

const handleMenuOpenChange = (open: boolean) => {
  if (open) {
    isMenuOpen.value = true
    return
  }
  // Released a frame late: the popover's focus-outside fires as the select
  // hands focus back, which is after this event and before the next paint.
  requestAnimationFrame(() => (isMenuOpen.value = false))
}

const handleDismiss = (event: Event) => {
  if (isMenuOpen.value) event.preventDefault()
}

const activeFormat = computed(() => props.format || dateFormat.value)

const dateLocale = computed(() => {
  const locale = DATE_FORMAT_LOCALE[activeFormat.value as DateFormat]
  if (locale) return locale

  /*
   * DATE_FORMATS is the closed set the settings screen writes, so an unmapped
   * format means something upstream stored one that is not on offer. Said out
   * loud rather than silently drawing the segments in some other order — a
   * date field that shows d/m where the user reads m/d is worse than an error.
   */
  console.warn(`[DSDateField] no locale orders the date format "${activeFormat.value}"`)
  return DATE_FORMAT_LOCALE[DATE_FORMATS[0] as DateFormat]
})

/*
 * The one shape both the segments and the calendar read. An unparseable model
 * simply selects nothing, rather than throwing on the way to a render.
 */
const dateValue = computed<DateValue | undefined>(() => {
  if (!props.modelValue) return undefined
  try {
    return parseDate(props.modelValue)
  } catch {
    return undefined
  }
})

const emitIso = (iso?: string) => {
  emits('update:modelValue', iso)
}

/*
 * Reka commits only once every segment holds a value, so there is no half-typed
 * state to validate and nothing to revert — which is why this has no draft, no
 * commit-on-blur and no Esc handler, unlike the masked field it replaces.
 */
const handleFieldUpdate = (next: DateValue | undefined) => {
  emitIso(next ? next.toString() : undefined)
}

/*
 * Copies the date the user can see, in the format they are reading it in.
 *
 * Written straight to the clipboard rather than left to the browser: a copy
 * takes the current selection, and a segment gets focus without one ever being
 * made, so the default would copy nothing at all. The keydown is the user
 * gesture the Clipboard API requires.
 *
 * The displayed shape, not the ISO model — copying "09/03/2026" out of a field
 * showing "09/03/2026" is the only unsurprising answer, and it pastes back into
 * this field unchanged, since the parser tries the user's format first.
 */
const handleCopy = () => {
  const text = stringToLocalDateString(props.modelValue, {
    customFormat: activeFormat.value,
  })
  // An empty field has nothing to copy, and must not wipe what is on the
  // clipboard to say so.
  if (!text) return

  navigator.clipboard.writeText(text).catch(e => handleCatchedError(e as string))
}

/*
 * Lets the clipboard shortcuts reach their handlers.
 *
 * Reka's segment keydown handler ends in `if (e.key !== TAB) e.preventDefault()`
 * — every key, ⌘V and ⌘C included. A prevented keydown means the browser never
 * performs the paste, so no `paste` event is produced anywhere and the handler
 * below could never run: pasting a date into this field silently did nothing.
 *
 * Capture phase on the group, so this runs before the segment's own listener,
 * and `stopPropagation` keeps Reka from seeing the event at all. Only these two
 * combinations are intercepted; every other key still belongs to Reka.
 *
 * Paste is handed back to the browser — it produces the `paste` event that
 * carries the text. Copy is done here, because there is no selection for the
 * browser to act on.
 */
const handleKeydown = (event: KeyboardEvent) => {
  if (!(event.metaKey || event.ctrlKey)) return

  // `code` rather than `key`: with a modifier held, layouts disagree on `key`.
  const isShortcut = (code: string, key: string) =>
    event.code === code || event.key.toLowerCase() === key

  if (isShortcut('KeyV', 'v')) {
    event.stopPropagation()
    return
  }

  if (isShortcut('KeyC', 'c')) {
    event.stopPropagation()
    event.preventDefault()
    handleCopy()
  }
}

/*
 * Segments are contenteditable, so a paste would otherwise drop raw characters
 * into whichever part has focus. Routed through the app's own parser instead,
 * which reads the user's format, ISO, and the two dozen shapes in
 * PASTE_DATE_FORMATS — so "Jan 15, 2026" pasted from a statement lands.
 *
 * Ambiguity is refused rather than guessed, and the field is cleared so the
 * paste cannot look like it worked. Mirrors UIInputDate deliberately: pasting a
 * date should behave the same in both halves of the app.
 */
const handlePaste = (event: ClipboardEvent) => {
  if (props.disabled) return
  event.preventDefault()

  const pastedText = event.clipboardData?.getData('text/plain')
  if (!pastedText) return

  const { isoDate, isAmbiguous } = parseDateText(pastedText)
  if (!isoDate) return

  if (isAmbiguous) {
    warn({
      message: 'Unable to paste date',
      description: 'The pasted date is ambiguous and can have multiple formats.',
    })
    emitIso(undefined)
    return
  }

  emitIso(isoDate)
}

const applyIso = (iso?: string) => {
  emitIso(iso)
  isOpen.value = false
}

const handleCalendarUpdate = (next: unknown) => {
  const date = next as CalendarDate | undefined
  applyIso(date ? date.toString() : undefined)
}

const SHORTCUTS: { label: string, resolve: () => DateTime }[] = [
  { label: 'Today', resolve: () => getCurrentDate() },
  { label: 'Last week', resolve: () => getLastWeek(getCurrentDate()) },
  { label: 'Last month', resolve: () => getLastMonth(getCurrentDate()) },
  { label: 'Last year', resolve: () => getLastYear(getCurrentDate()) },
]

const handleShortcut = (resolve: () => DateTime) => {
  applyIso(dateTimeToString(resolve(), { iso: true }) || undefined)
}

/*
 * Digits per segment, so a filled field reads like the placeholder it replaced:
 * "mm/dd/yyyy" becomes "09/03/2026", not "9/3/2026".
 *
 * Reka cannot do this. Its formatter hardcodes `numeric` for every part and
 * builds each segment with `formatter.part(date, part)` — no options argument,
 * no prop on the root — so the value it hands us is whatever the locale's
 * shortest numeric form is. Since the segment's text is already this
 * component's to render, the padding belongs here rather than in a fork of the
 * formatter.
 */
const SEGMENT_DIGITS: Record<string, number> = {
  day: 2,
  month: 2,
  year: 4,
}

/*
 * Only actual digits are padded: an empty segment's value is the localized
 * placeholder ("mm", "yyyy"), which must survive untouched — and does, because
 * it is not a number.
 */
const segmentText = (part: string, value: string) => {
  const width = SEGMENT_DIGITS[part]
  if (!width || !/^\d+$/.test(value)) return value

  return value.padStart(width, '0')
}

</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverAnchor as-child>
      <!--
        The wrapper is the control, so the ring belongs to it — the segments and
        the calendar button are parts of one field. The focus rule is the
        segments' own `:focus`, not `:focus-visible`: a segment is edited from
        the keyboard however it was reached, so a click has to show the ring too.
      -->
      <InputGroup
        :class="cn(
          'has-[[data-reka-date-field-segment]:focus]:border-ring',
          'has-[[data-reka-date-field-segment]:focus]:shadow-ds-focus',
          disabled && 'bg-muted cursor-not-allowed',
          props.class,
        )"
      >
        <DateFieldRoot
          v-slot="{ segments }"
          v-bind="$attrs"
          :model-value="dateValue"
          :locale="dateLocale"
          :disabled
          granularity="day"
          class="flex flex-1 items-center h-10 min-w-0 px-3 text-base font-medium tabular-nums text-foreground"
          @update:model-value="handleFieldUpdate"
          @keydown.capture="handleKeydown"
          @paste="handlePaste"
        >
          <template
            v-for="(segment, index) in segments"
            :key="index"
          >
            <span
              v-if="segment.part === 'literal'"
              class="px-px text-muted-foreground"
            >
              {{ segment.value }}
            </span>
            <!--
              The segment's text is the caller's to render — DateFieldInput
              draws the box and the behaviour, never the content. Leaving the
              slot empty renders an empty field with only the separators, which
              is exactly what it looks like. `segment.value` is the digits when
              the part has one and the localized placeholder ("yyyy", "mm",
              "dd") when it does not — zero-padded on the way out, so a filled
              field lines up with the placeholder it replaced. See segmentText.
            -->
            <DateFieldInput
              v-else
              :part="segment.part"
              class="px-0.5 rounded-ds-sm outline-none
                data-placeholder:text-muted-foreground
                focus:bg-accent focus:text-accent-foreground"
            >
              {{ segmentText(segment.part, segment.value) }}
            </DateFieldInput>
          </template>
        </DateFieldRoot>

        <InputGroupAddon align="inline-end">
          <PopoverTrigger as-child>
            <InputGroupButton
              :size
              :disabled
              aria-label="Open calendar"
            >
              <CalendarIcon class="text-muted-foreground" />
            </InputGroupButton>
          </PopoverTrigger>
        </InputGroupAddon>
      </InputGroup>
    </PopoverAnchor>

    <PopoverContent
      class="flex p-0"
      align="start"
      @escape-key-down="handleDismiss"
      @focus-outside="handleDismiss"
      @interact-outside="handleDismiss"
      @pointer-down-outside="handleDismiss"
    >
      <!--
        Shortcut rail, as in the legacy picker. Buttons rather than a list so
        each is reachable by keyboard on the way to the grid.
      -->
      <div
        v-if="!hideShortcuts"
        class="flex flex-col shrink-0 gap-0.5 p-2 border-r border-border"
      >
        <Button
          v-for="shortcut in SHORTCUTS"
          :key="shortcut.label"
          variant="ghost"
          size="sm"
          class="justify-start font-medium text-foreground hover:text-accent-foreground"
          @click="handleShortcut(shortcut.resolve)"
        >
          {{ shortcut.label }}
        </Button>
      </div>
      <DateFieldCalendar
        :model-value="dateValue"
        initial-focus
        @menu-open-change="handleMenuOpenChange"
        @update:model-value="handleCalendarUpdate"
      />
    </PopoverContent>
  </Popover>
</template>
